import { useQuery } from 'react-query';
import api from 'api';
import attendance from 'api/attendance';
import type { PreviewEntryInput } from 'api/gql-utils/overtime/types';
import type { OvertimeSnapshotEntry } from './types';

type OvertimePreviewResult = {
  amountByCardId: Map<string, number>;
  total: number;
};

export const useOvertimePreview = ({
  entries,
  userId,
  payrollMonth,
  enabled,
}: {
  entries: OvertimeSnapshotEntry[];
  userId: number | null;
  payrollMonth: string; // snapshot month — 'Y-m' (bulk) or 'Y-m-d'
  enabled: boolean;
}): { amountByCardId: Map<string, number>; total: number | null; isLoading: boolean } => {
  const hasEntries = entries.length > 0 && userId != null && Boolean(payrollMonth);

  const previewPayrollMonth = payrollMonth.substring(0, 7); // 'Y-m'

  const { data: attendanceDefaults, isLoading: isLoadingAttendance } = useQuery({
    queryKey: ['attendance-working-days', payrollMonth],
    queryFn: () => attendance.getWorkingDays(payrollMonth),
    enabled: enabled && hasEntries,
  });

  const { data: formulaCards, isLoading: isLoadingCards } = useQuery({
    queryKey: ['overtime-formula-cards'],
    queryFn: () => api.overtime.queries.listFormulaCards({}),
    select: (response) => response?.overtimeListFormulaCards?.data?.otFormulaCards ?? [],
    enabled: enabled && hasEntries,
  });

  const baseDays = String(attendanceDefaults?.data.baseDays ?? 0);
  const baseHours = String(attendanceDefaults?.data.baseHours ?? 0);

  const cardById = new Map((formulaCards ?? []).map((card) => [card.id, card]));


  const overrideAmountByCardId = new Map<string, number>();
  let overrideTotal = 0;
  entries.forEach((entry) => {
    if (entry.overrideAmount !== null && entry.overrideAmount !== undefined) {
      const rupees = entry.overrideAmount / 100;
      overrideAmountByCardId.set(entry.formulaCardId, rupees);
      overrideTotal += rupees;
    }
  });

  const previewableEntries = entries.filter(
    (entry) => entry.overrideAmount === null || entry.overrideAmount === undefined,
  );

  const previewEntries: PreviewEntryInput[] | null = (() => {
    if (!hasEntries || isLoadingAttendance || isLoadingCards || !formulaCards) {
      return null;
    }
    if (previewableEntries.length === 0) {
      return null; // every entry is overridden — nothing to preview
    }
    const built: PreviewEntryInput[] = [];
    for (const entry of previewableEntries) {
      const card = cardById.get(entry.formulaCardId);
      if (!card) {
        return null; // a card is missing — cannot resolve this request's amounts
      }
      built.push({
        formulaCardId: entry.formulaCardId,
        formulaCardName: card.name,
        formulaTemplate: card.formulaTemplate,
        otHours: entry.totalOtHours || '0',
        otDays: entry.totalOtDays || '0',
        workingHoursPerDay: entry.workingHoursPerDay || baseHours,
        baseDays,
      });
    }
    return built;
  })();

  const { data: preview, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['overtime-get-preview', userId, previewPayrollMonth, previewEntries],
    queryFn: () =>
      api.overtime.queries.getPreview({
        input: {
          userId: userId as number,
          payrollMonth: previewPayrollMonth,
          entries: previewEntries as PreviewEntryInput[],
        },
      }),
    enabled: enabled && previewEntries !== null && previewEntries.length > 0,
  });

  const isLoading =
    isLoadingAttendance ||
    isLoadingCards ||
    (previewEntries !== null && previewEntries.length > 0 && isLoadingPreview);


  const hasAnyAmount = overrideAmountByCardId.size > 0 || preview?.overtimeGetPreview?.data?.entries;
  let result: OvertimePreviewResult | null = null;

  if (hasAnyAmount) {
    const amountByCardId = new Map<string, number>(overrideAmountByCardId);
    let total = overrideTotal;
    (preview?.overtimeGetPreview?.data?.entries ?? []).forEach((previewEntry) => {
      if (overrideAmountByCardId.has(previewEntry.formulaCardId)) {
        return;
      }
      const rupees = (previewEntry.computedAmount ?? 0) / 100;
      amountByCardId.set(previewEntry.formulaCardId, rupees);
      total += rupees;
    });
    result = { amountByCardId, total };
  }

  return {
    amountByCardId: result?.amountByCardId ?? new Map<string, number>(),
    total: result?.total ?? null,
    isLoading,
  };
};
